import styled, { css } from "styled-components/macro";
import {
  Box,
  BoxContent,
  BoxFooter,
  BoxHeader,
  BoxNavLeft,
  BoxNavRight,
  BoxTitle,
  LoadingBoxContent,
} from "../Box";
import Stack from "../Stack";
import { Suspense, useState } from "react";
import Button from "../Button";
import { useFields } from "../../hooks/use-fields";
import Input, { ErrorMessage } from "../Input";
import { OnChange } from "../../hooks/use-optional-state";
import { THEME_COLOR_DARK, THEME_COLOR_DARK_ALT } from "../../vars";
import { MediumTitle } from "../Title";
import { CA_PROVINCES, US_STATES } from "./constants";
import Checkbox from "../Checkbox";
import { useSkipioQuery } from "../../hooks/use-query";
import { ALLOWED_USE_CASES } from "./constants";
import { TCRUseCase } from "./types";
import { useSkipioApiNonTransformed } from "../../api";
import { caseMethods, transformKey } from "../../api/case";
import usePlan from "../../hooks/use-plan";
import { useQueryClient } from "@tanstack/react-query";

type BrandFields = {
  hasTaxId: string;
  companyName: string;
  ein: string;
  einIssuingCountry: string;
  entityType: string;
  vertical: string;
  displayName: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  stockSymbol: string;
  stockExchange: string;
  website: string;
  email: string;
  phone: string;
};

type CampaignFields = {
  useCases: string[];
  description: string;
  sample1: string;
  sample2: string;
  sample3: string;
  sample4: string;
  sample5: string;
  messageFlow: string;
  ageGated: boolean;
  directLending: boolean;
  subscriberOptin: boolean;
  affiliateMarketing: boolean;
};

type ValidationErrors = Record<string, string | string[]>;

type PageProps = {
  brandField: <K extends keyof BrandFields>(
    name: K
  ) => {
    onChange: OnChange<BrandFields[K]>;
    value: BrandFields[K];
  };
  campaignField: <K extends keyof CampaignFields>(
    name: K
  ) => {
    onChange: OnChange<CampaignFields[K]>;
    value: CampaignFields[K];
  };
  errors: ValidationErrors;
};

function Intro({ startForm }: { startForm: () => void }) {
  return (
    <IntroContent>
      <div>
        <Stack>
          <p>
            Before you can start sending messages with Skipio, you will need to
            submit information about your business.
          </p>
          <p>
            Mobile carriers (AT&T, Verizon, T-Mobile, etc.) require this
            information in order to protect consumers and provide businesses
            with faster and more reliable text message services.
            <strong>
              {
                " Inaccurate information can result in undelivered messages and fees."
              }
            </strong>
          </p>
          <p>
            Once you have submitted your business information, you will be able
            to schedule a meeting with Skipio to answer any questions and go
            over your information before it is sent to the mobile carriers for
            review.
          </p>
          <p>
            Email <a href="mailto:support@skipio.com">support@skipio.com</a> for
            help.
          </p>
        </Stack>
      </div>
    </IntroContent>
  );
}

function TaxInfo({ brandField, errors }: PageProps) {
  return (
    <Stack>
      <MediumTitle>Tax ID Requirement</MediumTitle>
      <p>
        The US Carriers use your IRS issued EIN (US) or Canadian Business Number
        (CA) to verify your organization's identity. You must have an EIN or CA
        to send messages of a business nature to recipients in North America.
      </p>
      <div>
        <p>
          Does your organization have an EIN or other Tax ID?
          {errors.hasTaxId && (
            <ErrorMessage>{" " + errors.hasTaxId}</ErrorMessage>
          )}
        </p>
        <label>
          <input
            type="radio"
            checked={brandField("hasTaxId").value === "yes"}
            value="yes"
            onChange={(e) => brandField("hasTaxId").onChange(e.target.value)}
          />
          Yes
        </label>
        <label>
          <input
            type="radio"
            checked={brandField("hasTaxId").value === "no"}
            value="no"
            onChange={(e) => brandField("hasTaxId").onChange(e.target.value)}
          />
          No
        </label>
      </div>
      {brandField("hasTaxId").value === "yes" && (
        <Stack>
          <p>
            Enter your business information exactly as it appears on{" "}
            <a href="https://www.irs.gov/pub/irs-pdf/fss4.pdf">IRS Form SS-4</a>{" "}
            or your Canadian Business Registration. It's very important that the
            information you provide matches those documents exactly!
          </p>
          <Input
            label="Legal Business Name"
            {...brandField("companyName")}
            error={errors.companyName}
          />
          <Stack horizontal>
            <Input
              label="EIN (or Canadian BN)"
              onChange={(value) => {
                brandField("ein").onChange(
                  value.replace(/\D/g, "").slice(0, 9)
                );
              }}
              value={brandField("ein").value}
              error={errors.ein}
            />
            <Input
              label="Issuing Country"
              type="select"
              {...brandField("einIssuingCountry")}
              error={errors.einIssuingCountry}
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
            </Input>
          </Stack>
        </Stack>
      )}
      {brandField("hasTaxId").value === "no" && (
        <p>
          No problem! To get you approved for texting without an EIN, we will
          need you to respond to a verification text message. Please add your
          mobile phone number on the following page.
        </p>
      )}
    </Stack>
  );
}

function BusinessType({ brandField, errors }: PageProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack>
        <MediumTitle>Organization Type</MediumTitle>
        <p>
          Please select the business type that best matches your organization:
        </p>
        {errors.entityType && (
          <p>
            <ErrorMessage>{errors.entityType}</ErrorMessage>
          </p>
        )}
        <EntityCardsContainer>
          <EntityCard
            onClick={(e) => {
              brandField("entityType").onChange("PRIVATE_PROFIT");
            }}
            selected={brandField("entityType").value === "PRIVATE_PROFIT"}
          >
            <EntityCardTitle>Private</EntityCardTitle>
            <ul>
              <li>LLCs, Corporations</li>
              <li>NOT listed with a major stock exchange</li>
              <li>Organizations with a "NOT FOR PROFIT" status</li>
            </ul>
          </EntityCard>
          <EntityCard
            onClick={(e) => {
              brandField("entityType").onChange("PUBLIC_PROFIT");
            }}
            selected={brandField("entityType").value === "PUBLIC_PROFIT"}
          >
            <EntityCardTitle>Public</EntityCardTitle>
            <ul>
              <li>
                <p>
                  Corporations listed with a major stock exchange that have a
                  Stock Symbol/Ticker
                </p>
              </li>
            </ul>
          </EntityCard>
          <EntityCard
            onClick={(e) => {
              brandField("entityType").onChange("NON_PROFIT");
            }}
            selected={brandField("entityType").value === "NON_PROFIT"}
          >
            <EntityCardTitle>Non-Profit</EntityCardTitle>
            <ul>
              <li>
                Only organizations with a "NON PROFIT" status that are
                501(c)(3/4/5/6)
              </li>
              <li>
                Organizations with the "NOT FOR PROFIT" status should instead
                register as a Private Company
              </li>
            </ul>
          </EntityCard>
          <EntityCard
            onClick={(e) => {
              brandField("entityType").onChange("GOVERNMENT");
            }}
            selected={brandField("entityType").value === "GOVERNMENT"}
          >
            <EntityCardTitle>Government</EntityCardTitle>
            <ul>
              <li>
                Organizations controlled by U.S. federal, state, or local
                governments
              </li>
              <li>
                For non-U.S. government organizations, please select "Private"
              </li>
            </ul>
          </EntityCard>
        </EntityCardsContainer>
      </Stack>
    </div>
  );
}

function BusinessProfile({ brandField, campaignField, errors }: PageProps) {
  const verticals = useSkipioQuery({
    queryKey: ["campaignRegistryEnums", "verticals"],
  }).data;
  const stockExchanges = useSkipioQuery({
    queryKey: ["campaignRegistryEnums", "stockExchanges"],
  }).data;

  return (
    <Stack>
      <MediumTitle>Organization Information</MediumTitle>
      <Input
        label="Organization Name"
        helpText="The brand/marketing/DBA name of your organization"
        {...brandField("displayName")}
        error={errors.displayName}
      />
      {brandField("entityType").value === "PUBLIC_PROFIT" && (
        <Stack horizontal>
          <Input
            label="Stock Symbol"
            {...brandField("stockSymbol")}
            error={errors.stockSymbol}
          />
          <Input
            label="Stock Exchange"
            type="select"
            {...brandField("stockExchange")}
            error={errors.stockExchange}
          >
            <option value=""></option>
            {stockExchanges.map((exchange: string) => (
              <option value={exchange}>{exchange}</option>
            ))}
          </Input>
        </Stack>
      )}
      <Stack horizontal>
        <Input
          label="Industry"
          type="select"
          helpText="Select the option that most closely matches your organization"
          {...brandField("vertical")}
          error={errors.vertical}
        >
          <option value=""></option>
          {Object.keys(verticals).map((vertical) => (
            <option
              value={transformKey(vertical, caseMethods.snake).toUpperCase()}
            >
              {verticals[vertical].displayName}
            </option>
          ))}
        </Input>
        <Input
          label="Country"
          type="select"
          {...brandField("country")}
          error={errors.country}
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </Input>
      </Stack>
      <Input label="Address" {...brandField("street")} error={errors.street} />
      <Stack horizontal>
        <Input label="City" {...brandField("city")} error={errors.city} />
        {brandField("country").value === "CA" ? (
          <Input
            label="Province"
            type="select"
            {...brandField("state")}
            error={errors.state}
          >
            <option value=""></option>
            {CA_PROVINCES.map((province) => {
              return <option value={province.value}>{province.label}</option>;
            })}
          </Input>
        ) : (
          <Input
            label="State"
            type="select"
            {...brandField("state")}
            error={errors.state}
          >
            <option value=""></option>
            {US_STATES.map((state) => {
              return <option value={state.value}>{state.label}</option>;
            })}
          </Input>
        )}
        <Input
          label="Zip Code"
          {...brandField("postalCode")}
          error={errors.postalCode}
        />
      </Stack>
      <Input
        label="Website"
        {...brandField("website")}
        error={errors.website}
      />
      <Stack horizontal>
        <Input
          label="Support Email"
          {...brandField("email")}
          error={errors.email}
        />
        <Input
          label="Support Phone Number"
          {...brandField("phone")}
          error={errors.phone}
        />
      </Stack>
    </Stack>
  );
}

function CampaignUseCases({ brandField, campaignField, errors }: PageProps) {
  const useCases: TCRUseCase[] = useSkipioQuery({
    queryKey: ["campaignRegistryEnums", "usecases"],
  }).data;
  const allowedUseCases = Object.fromEntries(
    Object.entries(useCases).filter(([useCase]) =>
      ALLOWED_USE_CASES.includes(useCase)
    )
  );
  const selectedUseCases = campaignField("useCases").value;

  return (
    <Stack>
      <MediumTitle>Messaging Use Case</MediumTitle>
      <p>
        Select the use case that best matches the messaging that you will be
        doing.
      </p>
      <p>
        You must select at least 1 and may select up to 5 use cases. If you are
        unsure if a use case applies to you, do not select it.
      </p>
      {errors.useCases && <ErrorMessage>{errors.useCases}</ErrorMessage>}
      {
        <Suspense fallback={<LoadingBoxContent />}>
          <Stack>
            {Object.keys(allowedUseCases).map((useCase) => {
              return (
                <Checkbox
                  value={selectedUseCases.includes(useCase)}
                  onChange={(checked) => {
                    if (checked) {
                      campaignField("useCases").onChange([
                        ...new Set([...selectedUseCases, useCase]),
                      ]);
                    } else {
                      campaignField("useCases").onChange(
                        selectedUseCases.filter(
                          (selectedUseCase) => selectedUseCase !== useCase
                        )
                      );
                    }
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {allowedUseCases[useCase].displayName}
                  </span>
                  <UseCaseDescription>
                    {allowedUseCases[useCase].description}
                  </UseCaseDescription>
                </Checkbox>
              );
            })}
          </Stack>
        </Suspense>
      }
    </Stack>
  );
}

function CampaignUseCaseDescription({
  campaignField,
  brandField,
  errors,
}: PageProps) {
  return (
    <Stack>
      <MediumTitle>Messaging Use Case</MediumTitle>
      <p>
        Please provide a general description of how you intend to use your
        messaging to accomplish your use cases.
      </p>
      <Input
        label="Use Case Description"
        rows={5}
        type="textarea"
        {...campaignField("description")}
        error={errors.description}
      />
    </Stack>
  );
}

function CampaignUseCaseSamples({
  campaignField,
  brandField,
  errors,
}: PageProps) {
  return (
    <Stack>
      <MediumTitle>Messaging Use Case</MediumTitle>
      <p>
        Please provide examples of messages you might send to accomplish your
        use cases.
      </p>
      <Input
        label="Sample Message 1"
        rows={3}
        type="textarea"
        {...campaignField("sample1")}
        error={errors.sample1}
      />
      <Input
        label="Sample Message 2"
        rows={3}
        type="textarea"
        {...campaignField("sample2")}
        error={errors.sample2}
      />
      {campaignField("useCases").value.length >= 3 && (
        <Input
          label="Sample Message 3"
          rows={3}
          type="textarea"
          {...campaignField("sample3")}
          error={errors.sample3}
        />
      )}
      {campaignField("useCases").value.length >= 4 && (
        <Input
          label="Sample Message 4"
          rows={3}
          type="textarea"
          {...campaignField("sample4")}
          error={errors.sample4}
        />
      )}
      {campaignField("useCases").value.length >= 5 && (
        <Input
          label="Sample Message 5"
          rows={3}
          type="textarea"
          {...campaignField("sample5")}
          error={errors.sample5}
        />
      )}
    </Stack>
  );
}

function OptIn({ campaignField, brandField, errors }: PageProps) {
  return (
    <Stack>
      <MediumTitle>Messaging Opt In</MediumTitle>
      <p>
        Describe how a consumer opts-in, therefore giving consent to receive
        your messages. The call-to-action must be explicitly clear and inform
        the consumer of the nature of your messaging. If multiple opt-in methods
        can be used for your messaging, you must list them all.
      </p>
      <p>Some examples are:</p>
      <ul>
        <li>Entering a telephone number through a website</li>
        <li>Clicking a button on a mobile webpage</li>
        <li>
          Sending a message from the consumer’s mobile device that contains an
          advertising keyword
        </li>
        <li>
          Initiating the text message exchange in which the Message Sender
          replies to the consumer only with responsive information
        </li>
        <li>
          Signing up at a point-of-sale (POS) or other Message Sender on-site
          location
        </li>
        <li>
          Opting-in over the phone using interactive voice response (IVR)
          technology.
        </li>
      </ul>
      <Input
        label="Describe how you get opt-in from your messages recipients before sending messages to them."
        rows={3}
        type="textarea"
        {...campaignField("messageFlow")}
        error={errors.messageFlow}
      />
    </Stack>
  );
}

function Attributes({ campaignField, brandField, errors }: PageProps) {
  return (
    <Stack>
      <MediumTitle>Messaging Content</MediumTitle>
      <p>
        Select any of the following that apply to the content of your messaging:
      </p>
      <Checkbox
        value={campaignField("subscriberOptin").value}
        onChange={campaignField("subscriberOptin").onChange}
      >
        <span style={{ fontWeight: 500 }}>Subscriber Opt-in</span>
        <UseCaseDescription>
          Check this box if you require consumers to opt-in before receiving
          messaging
        </UseCaseDescription>
      </Checkbox>
      <Checkbox
        value={campaignField("affiliateMarketing").value}
        onChange={campaignField("affiliateMarketing").onChange}
      >
        <span style={{ fontWeight: 500 }}>Affiliate Marketing</span>
      </Checkbox>
      <Checkbox
        value={campaignField("directLending").value}
        onChange={campaignField("directLending").onChange}
      >
        <span style={{ fontWeight: 500 }}>
          Direct Lending or Loan Arrangement
        </span>
      </Checkbox>
      <Checkbox
        value={campaignField("ageGated").value}
        onChange={campaignField("ageGated").onChange}
      >
        <span style={{ fontWeight: 500 }}>Age Gated Content</span>
        <UseCaseDescription>
          Intense profanity, violence, graphic sexual activity, nudity, hate
          speech, or graphic depiction of illegal drug use
        </UseCaseDescription>
      </Checkbox>
    </Stack>
  );
}

function Summary({ campaignField, brandField, errors }: PageProps) {
  const plan = usePlan();
  return (
    <Stack>
      <MediumTitle>Registration Submitted</MediumTitle>
      <p>Your account registration has been successfully submitted!</p>
      <p>
        Next, you will need to schedule a meeting with a Skipio team member to
        review your registration and answer any questions you might have.
      </p>
      {brandField("hasTaxId").value !== "yes" && !plan.isMk && (
        <p>
          <span style={{ fontWeight: 500 }}>
            Don't forget - you will need an EIN (or Canadian BN) to complete
            registration and begin sending messages.
          </span>
          You can easily get one on the{" "}
          <a href="https://sa.www4.irs.gov/modiein/individual/index.jsp">
            IRS website
          </a>
        </p>
      )}
    </Stack>
  );
}

function InterviewSchedule() {
  const plan = usePlan();
  if (plan.isMk) {
    return (
      <iframe
        title="Schedule Meeting With Skipio"
        src="https://meetings.hubspot.com/courtney-smith2/skipio-registration-assistance-phone-call"
        width="100%"
        height="100%"
      />
    );
  } else {
    return (
      <iframe
        title="Schedule Meeting With Skipio"
        src="https://meetings-na2.hubspot.com/courtney-smith2/a2p-registration-call-"
        width="100%"
        height="100%"
      />
    );
  }
}

export default function BusinessQuestionnaire() {
  const skipioApi = useSkipioApiNonTransformed();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { field: brandField, value: brandValue } = useFields({
    initialValue: {
      hasTaxId: "",
      companyName: "",
      ein: "",
      einIssuingCountry: "US",
      entityType: "",
      vertical: "",
      displayName: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      stockSymbol: "",
      stockExchange: "",
      website: "",
      email: "",
      phone: "",
    },
  });
  const { field: campaignField, value: campaignValue } = useFields({
    initialValue: {
      useCases: [] as Array<string>,
      description: "",
      sample1: "",
      sample2: "",
      sample3: "",
      sample4: "",
      sample5: "",
      messageFlow: "",
      ageGated: false,
      directLending: false,
      subscriberOptin: false,
      affiliateMarketing: false,
    },
  });

  const submitForm = async () => {
    let useCase;
    let subUseCases: string[] = [];
    if (campaignValue.useCases.length > 1) {
      useCase = "MIXED";
      subUseCases = campaignValue.useCases.map((useCase) =>
        transformKey(useCase, caseMethods.snake).toUpperCase()
      );
    } else {
      useCase = transformKey(
        campaignValue.useCases[0],
        caseMethods.snake
      ).toUpperCase();
    }

    setIsSubmitting(true);
    await skipioApi.draft_brand.put(
      {},
      {
        data: {
          draft_brand: { ...brandValue },
        },
      }
    );
    await skipioApi.draft_campaign.put(
      {},
      {
        data: {
          draft_campaign: {
            usecase: useCase,
            subUsecases: subUseCases,
            embeddedLink: true,
            embeddedPhone: true,
            termsAndConditions: true,
            ...campaignValue,
          },
        },
      }
    );
    setIsSubmitting(false);

    queryClient.invalidateQueries({ queryKey: ["users", "me"] });
  };

  const handleNextPage = () => {
    const hasTaxId = brandValue.hasTaxId === "yes";
    setErrors({});
    const errors: Record<string, string> = {};

    switch (page) {
      case 1:
        if (!brandValue.hasTaxId) {
          errors.hasTaxId = "Please answer Yes or No";
        } else if (hasTaxId) {
          ["companyName", "ein"].forEach((fieldName) => {
            // @ts-ignore
            if (!brandValue[fieldName]) {
              errors[fieldName] = "This field is required";
            } else if (brandValue.ein.length !== 9) {
              errors.ein = "Please enter a valid EIN or Canadian BN";
            }
          });
        }
        break;
      case 2:
        if (!brandValue.entityType) {
          errors.entityType = "You must select an organization type";
        }
        break;
      case 3:
        if (brandValue.entityType === "PUBLIC_PROFIT") {
          ["stockSymbol", "stockExchange"].forEach((fieldName) => {
            // @ts-ignore
            if (!brandValue[fieldName]) {
              errors[fieldName] = "This field is required";
            }
          });
        }
        [
          "displayName",
          "vertical",
          "street",
          "city",
          "state",
          "postalCode",
          "website",
          "email",
          "phone",
        ].forEach((fieldName) => {
          // @ts-ignore
          if (!brandValue[fieldName]) {
            errors[fieldName] = "This field is required";
          }
        });
        break;
      case 4:
        if (!campaignValue.useCases.length) {
          errors.useCases = "You must select at least one use case";
        }
        break;
      case 5:
        if (!campaignValue.description) {
          errors.description = "This field is required";
        }
        break;
      case 6:
        if (!campaignValue.sample1) {
          errors.sample1 = "Please provide at least one sample message";
        }
        break;
      case 7:
        if (!campaignValue.messageFlow) {
          errors.messageFlow = "This field is required";
        }
        break;
    }

    if (Object.keys(errors).length) {
      setErrors({ ...errors });
      return;
    }

    // If they don't have an EIN already, they will probably be private profit
    if (page === 1 && !hasTaxId) {
      brandField("entityType").onChange("PRIVATE_PROFIT");
      setPage(page + 2);
      return;
    }

    if (page === 10) {
      submitForm();
      return;
    }

    setPage(page + 1);
  };

  const handlePrevPage = () => {
    const hasTaxId = brandValue.hasTaxId === "true";

    if (page === 3 && !hasTaxId) {
      setPage(page - 2);
      return;
    }

    setPage(page - 1);
  };

  const renderPageContent = () => {
    switch (page) {
      case 0:
        return <Intro startForm={() => setPage(1)} />;
      case 1:
        return (
          <TaxInfo
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 2:
        return (
          <BusinessType
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 3:
        return (
          <BusinessProfile
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 4:
        return (
          <CampaignUseCases
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 5:
        return (
          <CampaignUseCaseDescription
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 6:
        return (
          <CampaignUseCaseSamples
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 7:
        return (
          <OptIn
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 8:
        return (
          <Attributes
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 9:
        return (
          <Summary
            errors={errors}
            brandField={brandField}
            campaignField={campaignField}
          />
        );
      case 10:
        return <InterviewSchedule />;
    }
  };

  return (
    <Box>
      <BoxContent style={{ display: "flex", justifyContent: "center" }}>
        <Form>
          <BoxHeader>
            <BoxNavLeft>
              <BoxTitle>
                {page === 10 ? "Schedule Meeting" : "Account Registration"}
              </BoxTitle>
            </BoxNavLeft>
          </BoxHeader>
          <BoxContent>{renderPageContent()}</BoxContent>
          <BoxFooter>
            {page > 0 && page < 9 && (
              <BoxNavLeft>
                <Button onClick={handlePrevPage} variant="secondary">
                  Back
                </Button>
              </BoxNavLeft>
            )}
            <BoxNavRight>
              <Button onClick={handleNextPage} isLoading={isSubmitting}>
                {page === 10 ? "Finish" : "Next"}
              </Button>
            </BoxNavRight>
          </BoxFooter>
        </Form>
      </BoxContent>
    </Box>
  );
}

const Form = styled(Box)`
  width: 800px;
  & ul {
    margin-left: 16px;
  }
  & li {
    list-style-type: disc;
    list-style-position: outside;
    margin-bottom: 8px;
    line-height: 1.2em;
  }
`;

const IntroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.5em;
  color: #676163;
  strong {
    font-weight: bold;
  }
`;

const EntityCardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  margin: 16px;
  gap: 32px;
  justify-items: center;
  align-items: center;
`;

const EntityCard = styled.div<{ selected: boolean }>`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.1), 0 1.5px 3px rgba(0, 0, 0, 0.1);
  padding: 16px;
  cursor: pointer;
  &:hover {
    ${(p) =>
      !p.selected &&
      css`
        box-shadow: 0 0 0px 1.5px rgba(0, 0, 0, 0.15),
          0 1.5px 3px rgba(0, 0, 0, 0.3);
      `}
  }
  ${(p) =>
    p.selected &&
    css`
      box-shadow: 0 0 0px 2px ${THEME_COLOR_DARK};
    `}
`;

const EntityCardTitle = styled.p`
  text-align: center;
  color: ${THEME_COLOR_DARK_ALT};
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 24px;
`;

const UseCaseDescription = styled.span`
  margin-left: 8px;
  font-size: 12px;
  color: #676163;
`;
